import listeners from '../listeners';
import canvasUtils from '../utils';
import controllerUtils from './controllerUtils';
import hoverElement from './hoverElement';

export default class CanvasController {
  constructor(eventBus) {
    this.eventBus = eventBus;
    this.menu = null;
    this.hoveredComponent = null;
    this.hoverElement = null;
    this.selectedComponent = null;
    this.selectedHoverElement = null;
    this.selectedComponentParent = null;
    this.selectedParentHoverElement = null;
    this.dragButton = null;
    this.refreshing = false;
    this.queuedRefreshRequest = false;
    this.dragHandlesActive = false;
    this.dblClickComponents = ['button main-label', 'button sub-label'];
  }

  setCanvas(canvas) {
    this.canvas = canvas;
    this.parentDocument = this.canvas.defaultView.parent.document;
    listeners.removeListeners('canvas');
    this.registerListeners();
    this.refresh();
  }

  registerListeners() {
    this.registerCustomEventListeners();
    controllerUtils.registerOutOfFrameListeners(
      this.canvas,
      this.canvas.defaultView
    );
    listeners.register(
      this.canvas,
      'mousemove',
      (e) => {
        this.setHoverState(e);
        if (this.parentDocument)
          this.parentDocument.dispatchEvent(new MouseEvent(e.type, e));
      },
      'canvas'
    );
    listeners.register(
      this.canvas,
      'click',
      (e) => {
        this.handleClick(e);
      },
      'canvas'
    );
    listeners.register(
      this.canvas,
      'dblclick',
      (e) => {
        this.handleDblClick(e);
      },
      'canvas'
    );
    listeners.register(
      this.canvas.defaultView,
      'resize',
      (val) => {
        setTimeout(() => {
          this.refresh(val?.detail?.edit);
        });
      },
      'canvas'
    );
  }

  registerCustomEventListeners() {
    listeners.register(
      this.eventBus,
      'select-element',
      (e) => {
        if (e && e.childrenSelected) {
          [this.textElement] = e.childrenSelected;
        }
        this.selectComponent(e && e.id);
      },
      'canvas'
    );
    listeners.register(
      this.eventBus,
      'text-selection-changed',
      () => {
        this.selectComponent(this.selectedComponent, true);
      },
      'canvas'
    );
    listeners.register(
      this.eventBus,
      'add-drag-handles',
      () => {
        this.addDragHandles();
      },
      'canvas'
    );
  }

  addDragHandles() {
    if (this.dragHandlesActive) {
      return;
    }
    this.dragHandlesActive = true;
    if (this.canvas.getElementsByClassName('gp-component-hover').length)
      this.canvas
        .getElementsByClassName('gp-component-hover')[0]
        .classList.add('gp-component-hover-hide');
    let element = canvasUtils
      .getComponent(this.selectedComponent, this.eventBus, this.canvas)
      .getElement();
    if (this.textElement) {
      let targetedElement = this.textElement;
      if (this.targetedElement !== targetedElement)
        targetedElement = this.targetedElement;
      element = this.canvas.querySelector(
        `.gp-component-id-${targetedElement}`
      );
    }
    this.canvas.defaultView.addDragHandles(element, this.eventBus);
  }

  getHoveredComponentParent() {
    if (!this.hoverElement) return null;
    return canvasUtils
      .getComponent(this.hoverElement, this.eventBus, this.canvas)
      .parentComponent();
  }

  setHoverState(e) {
    if (
      this.canvas.body.classList.contains('draggable--is-dragging') ||
      this.selectedComponent
    ) {
      if (this.hoverElement) this.hoverElement.remove();
      this.hoverElement = null;
      return;
    }
    const hoveredComponent = controllerUtils.getHoveredComponent(e);
    if (
      hoveredComponent !== this.hoveredComponent &&
      !e.target.closest('.gp-component-menu')
    ) {
      this.hoveredComponent = hoveredComponent;
      this.setHoverElement();
      this.getComponentTree();
    }
  }

  setHoverElement(silent = false) {
    if (this.hoverElement) this.hoverElement.remove();
    this.hoverElement = null;
    this.dragButton = null;
    if (!this.hoveredComponent) return;
    const hoveredComponent = canvasUtils.getComponent(
      this.hoveredComponent,
      this.eventBus,
      this.canvas
    );
    if (!hoveredComponent) return;
    const element = hoveredComponent.getElement();
    if (
      this.hoverElement ||
      this.canvas.body.classList.contains('draggable--is-dragging') ||
      !element
    )
      return;
    this.hoverElement = hoverElement.add(element, silent);

    if (hoveredComponent.isBlock) {
      hoverElement.addUpDownButton(this.hoverElement, hoveredComponent);
    }
    this.hoverElement.classList.add('gp-component-hover-new');
  }

  handleDblClick(e) {
    if (this.dblClickComponents.includes(e.target.getAttribute('data-gp-component'))) {
      this.handleClick(e, true);
    }
  }

  handleClick(e, dblclick = false) {
    if (!e.target.closest('[data-gp-component]')) return;
    e.stopPropagation();
    e.preventDefault();
    if (!dblclick && this.dblClickComponents.includes(e.target.getAttribute('data-gp-component'))) return;

    const zoom = this.canvas.body.getAttribute('data-gp-zoom');
    if (zoom && parseInt(zoom) !== 1) {
      return;
    }

    const toSelect = e.target.closest('[data-gp-component-id]');
    if (!toSelect || toSelect.tagName === 'BODY') {
      return;
    }

    const component = toSelect.getAttribute('data-gp-component-id');
    if (component === this.selectedComponent) return;

    this.targetedElement = canvasUtils.getComponentIdFromClass(e.target);

    this.selectComponent(component);
    if (component === this.targetedElement) {
      this.eventBus.$emit('select-element-request', component);
    } else {
      this.eventBus.$emit('select-element-request', {
        component,
        targetedElement: this.targetedElement
      });
    }
  }

  selectComponent(component, force, silent = false) {
    if (this.selectedComponent !== component) {
      this.dragHandlesActive = false;
      this.canvas.defaultView.removeDragHandles();
    }
    this.selectedComponent = component;
    if (
      document.querySelector('.gp-quill-showing') &&
      document.querySelector('.gp-quill-showing').length
    ) {
      document.querySelector('.gp-quill-showing').forEach((box) => {
        box.classList.remove('gp-quill-showing');
      });
    }
    this.resetCanvasHoverState();
    if (!this.selectedComponent) {
      return;
    }
    this.getComponentTree();
    const selectedComponent = canvasUtils.getComponent(
      component,
      this.eventBus,
      this.canvas
    );
    const selectedElement = selectedComponent.getElement();

    if (!selectedComponent.textEditable) {
      this.selectedHoverElement = hoverElement.add(
        selectedElement,
        silent
      );
    } else {
      const selected = selectedElement.getAttribute('data-gp-component-id');
      if (force) {
        this.targetedElement = this.textElement;
      }
      const textElement = this.canvas.querySelector(
        `.gp-component-id-${selected}`
      );
      if (textElement) {
        this.selectedHoverElement = hoverElement.add(textElement, silent);
      }
    }
    this.canvas.querySelectorAll('.gp-blocks-adder--inner').forEach(blockAdder => blockAdder.classList.remove('active'));
    if (selectedComponent.isBlock) {
      const prevAdder = canvasUtils.getPrevSibling(selectedElement, '.gp-blocks-adder--inner');
      const nextAdder = canvasUtils.getNextSibling(selectedElement, '.gp-blocks-adder--inner');

      if (prevAdder) prevAdder.classList.add('active');
      if (nextAdder) nextAdder.classList.add('active');

      hoverElement.addUpDownButton(
        this.selectedHoverElement,
        selectedComponent
      );
    }
    this.selectedComponentParent = null;
    if (this.dragHandlesActive) {
      this.selectedHoverElement.classList.add('gp-component-hover-hide');
    }
    if (
      !selectedComponent.componentType.match(
        /^(Body)$/gi
      )
    )
      this.addComponentMenu(selectedComponent.draggable && !selectedElement.getAttribute('data-gp-block'));
  }

  addSelectedComponentParent(selectedComponent, silent) {
    this.selectedComponentParent = selectedComponent.parentComponent();
    const selectedComponentParent = canvasUtils.getComponent(
      this.selectedComponentParent,
      this.eventBus,
      this.canvas
    );
    const selectedComponentParentElement = selectedComponentParent.getElement();
    this.selectedParentHoverElement = hoverElement.add(
      selectedComponentParentElement,
      silent
    );
    this.selectedParentHoverElement.classList.add('gp-component-parent-hover');
    hoverElement.fitInside(
      this.selectedHoverElement,
      this.selectedParentHoverElement
    );
  }

  resetCanvasHoverState() {
    if (this.menu) {
      this.menu.remove();
      this.menu = null;
    }
    if (this.hoverElement) {
      this.hoverElement.remove();
      this.hoverElement = null;
    }
    if (this.selectedHoverElement) {
      this.selectedHoverElement.remove();
      this.selectedHoverElement = null;
    }
    if (this.selectedParentHoverElement) {
      this.selectedParentHoverElement.remove();
      this.selectedParentHoverElement = null;
    }
  }

  addComponentMenu(draggable) {
    const component = canvasUtils.getComponent(
      this.selectedComponent,
      this.eventBus,
      this.canvas
    );
    const extraItems = [];
    this.eventBus.$emit('canvas-get-menu', {
      menu: extraItems,
      component,
    });
    this.menu = component.getMenu(extraItems);
    hoverElement.addMenu(
      this.selectedHoverElement,
      this.menu,
      draggable,
      this.canvas,
      canvasUtils.getComponent(this.selectedComponent,this.eventBus,this.canvas).element
    );
  }

  refresh(silent = false) {
    if (this.refreshing) {
      this.queuedRefreshRequest = true;
      return;
    }
    this.refreshing = true;
    setTimeout(() => {
      this.refreshing = false;
      if (this.queuedRefreshRequest) {
        this.queuedRefreshRequest = false;
        this.refresh(silent);
      }
    }, 100);
    canvasUtils.triggerEvent(this.canvas, 'gp-canvas-resize');
    if (
      this.selectedComponent &&
      !canvasUtils.getComponent(
        this.selectedComponent,
        this.eventBus,
        this.canvas
      )
    ) {
      this.selectedComponent = null;
      this.selectedComponentParent = null;
      this.hoveredComponent = null;
      this.resetCanvasHoverState();
      this.eventBus.$emit('select-element-request', null);
      this.getComponentTree();
      return;
    }
    if (this.selectedComponent) {
      this.selectComponent(this.selectedComponent, null, silent);
    } else if (this.hoveredComponent) {
      this.setHoverElement(silent);
    }

    if( silent )
      this.canvas.querySelectorAll('.dndbefore').forEach(el => {
        el.classList.remove('dndbefore');
        el.classList.remove('dndbefore-top');
        el.classList.remove('dndbefore-middle');
        el.classList.remove('dndbefore-bottom');
      });
  }

  getComponentTree() {
    if (!this.hoveredComponent && !this.selectedComponent) {
      this.eventBus.$emit('dom-tree-update', null);
      return;
    }
    const tree = [];
    let { element } = canvasUtils.getComponent(
      this.selectedComponent || this.hoveredComponent,
      this.eventBus,
      this.canvas
    );
    while (element && element.tagName !== 'HTML') {
      const label = canvasUtils.getComponentLabel(element);
      if (label)
        tree.push({ label, id: element.getAttribute('data-gp-component-id') });
      element = element.parentNode;
    }
    this.eventBus.$emit('dom-tree-update', tree.reverse());
    // Clear the tree array
    tree.length = 0;
  }
}
